import React, { useEffect, useState } from "react";
import { ref, onValue, remove, update } from "firebase/database";
import { db } from "../firebase";
import AddPlayers from "./AddPlayers";
import EditPlayer from "./EditPlayer";
import PlayerRequests from "./PlayerRequests";
import useFile from "../hooks/useFile";
import Confirm from "./Confirm";
import Popup from "./Popup";

const PlayerList = ({ gameId }) => {
  const [players, setPlayers] = useState({});
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [auctionSettings, setAuctionSettings] = useState(null);
  const [currentPlayerCount, setCurrentPlayerCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [teams, setTeams] = useState([]);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const { deleteFile } = useFile();
  const [confirm, setConfirm] = useState({
    isOpen: false,
    message: "",
    title: "",
    onConfirm: null,
  });
  const [popup, setPopup] = useState({
    isOpen: false,
    message: "",
    title: "",
    type: "info",
  });

  const handleDeleteFile = async (fileId) => {
    const result = await deleteFile(fileId);
    return result;
  };

  const handleEdit = (playerId, playerData) => {
    setEditingPlayerId(playerId);
    setSelectedPlayer(playerData);
    setShowAddPlayer(true);
  };

  const handleCloseEdit = () => {
    setEditingPlayerId(null);
    setSelectedPlayer(null);
    setShowAddPlayer(false);
  };

  useEffect(() => {
    // Fetch all data in a single operation
    const gameRef = ref(db, `games/${gameId}`);

    const unsubscribe = onValue(gameRef, (snapshot) => {
      if (snapshot.exists()) {
        const gameData = snapshot.val();

        // Set auction settings
        if (gameData.auctionSettings) {
          setAuctionSettings(gameData.auctionSettings);
        }

        // Set teams
        if (gameData.teams) {
          const teamsArray = Object.entries(gameData.teams).map(
            ([id, team]) => ({
              ...team,
              id,
            })
          );
          setTeams(teamsArray);
        } else {
          setTeams([]);
        }

        // Set players
        if (gameData.players) {
          setPlayers(gameData.players);
          setCurrentPlayerCount(Object.keys(gameData.players).length);
        } else {
          setPlayers({});
          setCurrentPlayerCount(0);
        }
      }
    });

    return () => unsubscribe();
  }, [gameId]);

  useEffect(() => {
    // Listen to player requests
    const requestsRef = ref(db, `player-req/${gameId}/players`);

    const unsubscribe = onValue(requestsRef, (snapshot) => {
      if (snapshot.exists()) {
        setPendingRequestsCount(Object.keys(snapshot.val()).length);
      } else {
        setPendingRequestsCount(0);
      }
    });

    return () => unsubscribe();
  }, [gameId]);

  const handleDelete = async (playerId, fileId) => {
    setConfirm({
      isOpen: true,
      title: "Delete Player",
      message:
        "Are you sure you want to delete this player? This action cannot be undone.",
      type: "error",
      onConfirm: async () => {
        if (fileId) {
          await handleDeleteFile(fileId);
        }
        await remove(ref(db, `games/${gameId}/players/${playerId}`));
        setConfirm({ isOpen: false, message: "", title: "", onConfirm: null });
      },
    });
  };

  // Set player as upcoming
  const handleSetUpcoming = (player) => {
    setConfirm({
      isOpen: true,
      title: "Set Player as Upcoming",
      message: `Are you sure you want to set ${player.name} back to upcoming status? This will remove their current team assignment and sold price.`,
      type: "warning",
      onConfirm: async () => {
        try {
          const updates = {
            [`games/${gameId}/players/${player.id}/status`]: "upcoming",
            [`games/${gameId}/players/${player.id}/teamId`]: null,
            [`games/${gameId}/players/${player.id}/soldPrice`]: null,
            [`games/${gameId}/players/${player.id}/teamName`]: null,
            [`games/${gameId}/players/${player.id}/soldAt`]: null,
            [`games/${gameId}/players/${player.id}/isCaptain`]: false,
          };

          if (player.teamId) {
            updates[`games/${gameId}/teams/${player.teamId}/players/${player.id}`] = null;

            const team = teams.find((t) => t.id === player.teamId);
            if (team && player.soldPrice) {
              updates[`games/${gameId}/teams/${player.teamId}/purseLeft`] =
                Number(team.purseLeft) + Number(player.soldPrice);
            }
            if (team?.captainPlayerId === player.id) {
              updates[`games/${gameId}/teams/${player.teamId}/captainPlayerId`] = null;
              updates[`games/${gameId}/teams/${player.teamId}/captainPlayerName`] = null;
            }
          }

          await update(ref(db), updates);
        } catch (error) {
          console.error("Error updating player:", error);
          setPopup({
            isOpen: true,
            title: "Update Failed",
            message: "Failed to update player",
            type: "error",
          });
        }
        setConfirm({ isOpen: false, message: "", title: "", onConfirm: null });
      },
    });
  };

  // Get unique roles from players
  const uniqueRoles = [...new Set(Object.values(players).map((p) => p.role))];

  // Filter players based on search and filters
  const filteredPlayers = Object.keys(players).filter((id) => {
    const player = players[id];
    const matchesSearch =
      player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (player.playerNumber &&
        player.playerNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole = roleFilter === "all" || player.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" || player.status === statusFilter;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const canAddPlayer =
    !auctionSettings?.totalPlayers ||
    currentPlayerCount < auctionSettings.totalPlayers;
  const playersRemaining = auctionSettings?.totalPlayers
    ? auctionSettings.totalPlayers - currentPlayerCount
    : "Unlimited";

  return (
    <div className="card p-4 shadow mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="bi bi-people-fill me-2"></i>
          Players List
        </h4>
        <div className="d-flex gap-2">
          <button
            className="btn btn-info position-relative"
            onClick={() => setShowRequests(true)}
          >
            <i className="bi bi-inbox-fill me-2"></i>
            Player Requests
            {pendingRequestsCount > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                {pendingRequestsCount}
                <span className="visually-hidden">pending requests</span>
              </span>
            )}
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={async () => {
              const link = `${window.location.origin}/player-reg/${gameId}`;
              try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  await navigator.clipboard.writeText(link);
                } else {
                  const ta = document.createElement("textarea");
                  ta.value = link;
                  ta.style.position = "fixed";
                  ta.style.left = "-9999px";
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand("copy");
                  document.body.removeChild(ta);
                }
                setPopup({
                  isOpen: true,
                  title: "Link Copied",
                  message: "Invite link copied to clipboard.",
                  type: "success",
                });
              } catch (err) {
                setPopup({
                  isOpen: true,
                  title: "Copy Failed",
                  message: "Failed to copy invite link.",
                  type: "error",
                });
              }
            }}
            title="Share this link to players for registration"
          >
            <i className="bi bi-clipboard-fill me-2"></i>
            Copy Registration Link
          </button>
          <button
            className="btn btn-success"
            onClick={() => {
              setSelectedPlayer(null);
              setShowAddPlayer(!showAddPlayer);
            }}
            disabled={!canAddPlayer}
          >
            <i className="bi bi-plus-circle me-2"></i>
            {showAddPlayer
              ? "Hide Add Player"
              : `Add Player (${currentPlayerCount}/${
                  auctionSettings?.totalPlayers || "∞"
                })`}
          </button>
        </div>
      </div>

      {/* Player Stats Info */}
      {auctionSettings && (
        <div className="alert alert-info mb-4">
          <div className="row">
            <div className="col-md-4">
              <strong>
                <i className="bi bi-people me-2"></i>
                Total Players:
              </strong>
              <div className="fs-5">
                {auctionSettings.totalPlayers || "Not Set"}
              </div>
            </div>
            <div className="col-md-4">
              <strong>
                <i className="bi bi-check-circle me-2"></i>
                Current Players:
              </strong>
              <div className="fs-5">{currentPlayerCount}</div>
            </div>
            <div className="col-md-4">
              <strong>
                <i className="bi bi-bar-chart me-2"></i>
                Remaining Slots:
              </strong>
              <div className="fs-5 text-success">{playersRemaining}</div>
            </div>
          </div>
        </div>
      )}

      {/* Warning if limit reached */}
      {!canAddPlayer && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-octagon me-2"></i>
          <strong>Maximum player limit reached!</strong> You have{" "}
          {currentPlayerCount} out of {auctionSettings.totalPlayers} players.
        </div>
      )}

      {showAddPlayer && (
        <div className="mb-4">
          {selectedPlayer ? (
            <EditPlayer
              gameId={gameId}
              playerId={editingPlayerId}
              selectedPlayer={selectedPlayer}
              onClose={handleCloseEdit}
              basePrice={auctionSettings?.defaultBasePrice}
              teams={teams}
            />
          ) : (
            <AddPlayers
              gameId={gameId}
              basePrice={auctionSettings?.defaultBasePrice}
            />
          )}
        </div>
      )}

      {/* Search and Filter Section */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or player number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setSearchTerm("")}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="upcoming">Upcoming</option>
            <option value="sold">Sold</option>
            <option value="unsold">Unsold</option>
          </select>
        </div>

        <div className="col-md-4">
          <select
            className="form-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            {uniqueRoles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Role Filter Buttons */}
      <div className="mb-3 d-flex flex-wrap gap-2">
        <button
          className={`btn btn-sm ${
            roleFilter === "all" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setRoleFilter("all")}
        >
          <i className="bi bi-globe me-1"></i>
          All ({Object.keys(players).length})
        </button>
        {uniqueRoles.map((role) => {
          const count = Object.values(players).filter(
            (p) => p.role === role
          ).length;
          return (
            <button
              key={role}
              className={`btn btn-sm ${
                roleFilter === role ? "btn-info" : "btn-outline-info"
              }`}
              onClick={() => setRoleFilter(role)}
            >
              {role} ({count})
            </button>
          );
        })}
      </div>

      <div className="table-responsive">
        <table className="table table-hover table-striped align-middle">
          <thead className="table-primary">
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Player Number</th>
              <th>Role</th>
              <th>Base Price</th>
              <th>Status</th>
              <th>Team</th>
              <th>Mobile</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredPlayers.map((id, index) => {
              const p = players[id];
              const playerTeam = p.teamId
                ? teams.find((t) => t.id === p.teamId)
                : null;

              return (
                <tr key={id}>
                  <td className="fw-semibold">{index + 1}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <img
                        src={p.image || "/player.png"}
                        alt={p.name}
                        className="rounded-circle me-2"
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                        }}
                      />
                      <span className="fw-semibold">{p.name}</span>
                    </div>
                  </td>
                  <td>{p.playerNumber || "-"}</td>
                  <td>
                    <span className="badge bg-info text-dark">{p.role}</span>
                  </td>
                  <td className="fw-semibold text-success">৳ {p.price}</td>
                  <td>
                    <span
                      className={`badge ${
                        p.status === "sold"
                          ? "bg-success"
                          : p.status === "unsold"
                          ? "bg-danger"
                          : "bg-secondary"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td>
                    {playerTeam ? (
                      <span className="badge bg-primary">
                        {playerTeam.teamName}
                      </span>
                    ) : (
                      <span className="text-muted">Not Assigned</span>
                    )}
                  </td>
                  <td>{p.mobileNo || "-"}</td>
                  <td>
                    <div className="btn-group" role="group">
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleEdit(id, p)}
                        title="Edit Player"
                      >
                        <i className="bi bi-pencil-fill"></i>
                      </button>

                      {(p.status === "sold" || p.status === "unsold") && (
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleSetUpcoming({ ...p, id })}
                          title="Set as Upcoming"
                        >
                          <i className="bi bi-arrow-counterclockwise"></i>
                        </button>
                      )}

                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(id, p?.fileId)}
                        title="Delete Player"
                      >
                        <i className="bi bi-trash-fill"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {filteredPlayers.length === 0 && (
              <tr>
                <td colSpan="9" className="text-center text-muted py-4">
                  <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                  {searchTerm || roleFilter !== "all" || statusFilter !== "all"
                    ? "No players found matching your filters."
                    : "No players found. Add your first player!"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Results Summary */}
      {(searchTerm || roleFilter !== "all" || statusFilter !== "all") && (
        <div className="alert alert-light mt-3">
          <i className="bi bi-funnel me-2"></i>
          Showing {filteredPlayers.length} of {Object.keys(players).length}{" "}
          players
          {searchTerm && ` matching "${searchTerm}"`}
          {roleFilter !== "all" && ` with role "${roleFilter}"`}
          {statusFilter !== "all" && ` with status "${statusFilter}"`}
        </div>
      )}

      <Confirm
        isOpen={confirm.isOpen}
        onConfirm={confirm.onConfirm}
        onCancel={() =>
          setConfirm({ isOpen: false, message: "", title: "", onConfirm: null })
        }
        message={confirm.message}
        title={confirm.title}
        type={confirm.type || "warning"}
      />

      <Popup
        isOpen={popup.isOpen}
        onClose={() =>
          setPopup({ isOpen: false, message: "", title: "", type: "info" })
        }
        message={popup.message}
        title={popup.title}
        type={popup.type}
      />

      {showRequests && (
        <PlayerRequests
          gameId={gameId}
          onClose={() => setShowRequests(false)}
        />
      )}
    </div>
  );
};

export default PlayerList;
